/*
 *  -----------------------------------------------------------------------
 *    <copyright file="delete-schedule-version/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';

import { SVTScheduleVersionFields } from '@components/admin/schedule-versions-grid/schedule-versions-grid.interface';
import Button from '@components/core/buttons/button';
import CancelButton from '@components/core/buttons/cancel-button';
import { DialogActions } from '@components/core/dialog';
import { DeleteScheduleVersionDialogProps } from './delete-schedule-version.interface';
import { useTranslation } from 'hooks';
import { CustomDialog, DialogTitle } from 'views/components/core/dialog';
import './styles.scss';
import NotificationService from '@utils/notification';
import {
    saveStudyVisitScheduleVersion,
    saveStudyVisitScheduleVersionVariables,
    deleteStudyVisitSchedule,
    deleteStudyVisitScheduleVariables,
} from '@queries/generated/study-visit-schedules.generated';
import {
    DELETE_STUDY_VISIT_SCHEDULE,
    GET_STUDY_VISIT_SCHEDULES,
    SAVE_STUDY_VISIT_SCHEDULE_VERSION,
} from '@queries/study-visit-schedules';

const DeleteScheduleVersionDialog = ({
    close,
    studyId,
    currentSchedule,
    hasMultipleSchedules,
}: DeleteScheduleVersionDialogProps) => {
    const { t } = useTranslation();
    const [currentScheduleVersion, setCurrentScheduleVersion] = useState<SVTScheduleVersionFields | null>(null);

    const [deleteScheduleVersion] = useMutation<deleteStudyVisitSchedule, deleteStudyVisitScheduleVariables>(
        DELETE_STUDY_VISIT_SCHEDULE,
    );

    const [inactivateScheduleVersion] = useMutation<
        saveStudyVisitScheduleVersion,
        saveStudyVisitScheduleVersionVariables
    >(SAVE_STUDY_VISIT_SCHEDULE_VERSION);

    const onDeleteSchedule = () => {
        deleteScheduleVersion({
            variables: {
                studyId,
                visitScheduleId: currentSchedule?.id,
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then((data) => {
                if (data.data?.deleteStudyVisitSchedule) {
                    NotificationService.showNotification(t('admin:scheduleVersionDeleted'));
                } else {
                    NotificationService.showNotification(t('errorMessage'));
                }
                close();
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    const onInactivateSchedule = () => {
        inactivateScheduleVersion({
            variables: {
                studyId,
                visitScheduleVersion: {
                    id: currentScheduleVersion?.id,
                    name: currentScheduleVersion?.name,
                    description: currentScheduleVersion?.description,
                    studyId: currentScheduleVersion?.studyId,
                    cohortIds: currentScheduleVersion?.cohortIds,
                    treatmentArmIds: currentScheduleVersion?.treatmentArmIds,
                    active: false,
                    recordId: currentScheduleVersion?.recordId,
                    version: currentScheduleVersion?.version,
                    versionDate: {
                        ignore: 1,
                        time: currentScheduleVersion?.versionDate?.time,
                    },
                    groupId: currentScheduleVersion?.groupId,
                },
                reason: '',
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then((data) => {
                if (data) {
                    NotificationService.showNotification(t('admin:scheduleVersionInactivated'));
                } else {
                    NotificationService.showNotification(t('errorMessage'));
                }
                close();
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    useEffect(() => {
        if (currentSchedule) {
            setCurrentScheduleVersion(currentSchedule);
        }
    }, [currentSchedule]);

    return (
        <CustomDialog
            onClose={() => close()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open
            className="custom-width-popup"
        >
            <DialogTitle id="delete-schedule-modal-title">
                {t('admin:deleteVersion')} : {currentScheduleVersion?.recordId}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="schedule-dialog-content">
                <div className="delete-schedule-text">
                    {hasMultipleSchedules
                        ? t('admin:deleteMultipleVersions', {
                              version: currentScheduleVersion?.version,
                              schedule: currentScheduleVersion?.name,
                          })
                        : currentScheduleVersion?.active
                          ? t('admin:deleteOneVersion')
                          : t('admin:deleteMultipleVersions', {
                                version: currentScheduleVersion?.version,
                                schedule: currentScheduleVersion?.name,
                            })}
                </div>
                <DialogActions className="schedule-dialog-content-actions flex-end">
                    <CancelButton className="cancel-schedule-btn" text="Cancel" onClick={() => close()} />
                    {!hasMultipleSchedules && currentScheduleVersion?.active && (
                        <Button
                            id="inactivate-schedule-btn"
                            text={t('common:Inactivate')}
                            onClick={onInactivateSchedule}
                        />
                    )}
                    <Button id="delete-schedule-btn" text={t('common:Delete')} onClick={onDeleteSchedule} />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};
export default DeleteScheduleVersionDialog;
